import React from 'react';

import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';

import {downloadInvoicePDF} from '@entities/invoice/api';
import {Button, ButtonVariant} from '@shared/ui/Button';
import {Page} from '@shared/ui/Page';
import {Spinner} from '@shared/ui/Spinner';

const PDF_STYLE: React.CSSProperties = {
    width: '100vw',
    height: '100vh',
    border: 0,
    display: 'block',
};

const CenterStyle: React.CSSProperties = {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: 16,
    padding: '24px',
    textAlign: 'center',
};

const isMobileDevice = () => {
    if (typeof navigator === 'undefined') return false;

    return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(navigator.userAgent);
};

export const PdfViewerPage = () => {
    const {t} = useTranslation();
    const {id, tin} = useParams<{id: string; tin: string}>();
    const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const isMobile = React.useMemo(() => isMobileDevice(), []);
    const autoDownloadAttemptedRef = React.useRef<string | null>(null);

    const handleDownload = React.useCallback(() => {
        if (!pdfUrl) return;

        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${id ?? 'invoice'}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, [id, pdfUrl]);

    React.useEffect(() => {
        let isMounted = true;
        let nextUrl: string | null = null;

        const load = async () => {
            try {
                setError(null);
                setPdfUrl(null);

                const blob = await downloadInvoicePDF(id, tin);
                nextUrl = URL.createObjectURL(blob);

                if (!isMounted) return;
                setPdfUrl(nextUrl);
            } catch {
                if (isMounted) setError(t('page_pdf_viewer.error'));
            }
        };

        load();

        return () => {
            isMounted = false;
            if (nextUrl) URL.revokeObjectURL(nextUrl);
        };
    }, [id, t, tin]);

    React.useEffect(() => {
        if (!isMobile || !pdfUrl || autoDownloadAttemptedRef.current === pdfUrl) return;

        autoDownloadAttemptedRef.current = pdfUrl;
        handleDownload();
    }, [handleDownload, isMobile, pdfUrl]);

    let content = (
        <div style={CenterStyle}>
            <Spinner size={3} />
        </div>
    );

    if (error) {
        content = (
            <div style={CenterStyle}>
                <span>{error}</span>
            </div>
        );
    }

    if (pdfUrl && !isMobile) {
        content = <iframe title="pdf-viewer" src={pdfUrl} style={PDF_STYLE} />;
    }

    if (pdfUrl && isMobile) {
        content = (
            <div style={CenterStyle}>
                <span>{t('page_pdf_viewer.ready')}</span>
                <span>{t('page_pdf_viewer.mobile_autodownload')}</span>
                <span>{t('page_pdf_viewer.mobile_fallback')}</span>
                <Button variant={ButtonVariant.Secondary} onClick={handleDownload}>
                    {t('page_pdf_viewer.download')}
                </Button>
            </div>
        );
    }

    return <Page title={t('page_pdf_viewer.title')}>{content}</Page>;
};
